<template>
    <div>
        <!-- Backdrop -->
        <transition name="sb-fade">
            <div v-if="visible" class="sb-backdrop" @click="closeByBackdrop" aria-hidden="true" />
        </transition>

        <!-- Panel -->
        <transition name="sb-slide">
            <aside v-if="visible" class="sb-panel" role="dialog" aria-modal="true" :aria-label="ariaLabel" ref="panel"
                @keydown.esc.prevent="hide">
                <!-- Header -->
                <header class="sb-header">
                    <button class="sb-iconbtn" @click="hide" :title="$tClose">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                    <slot name="header">
                        <div class="sb-title">
                            <slot name="title">{{ title }}</slot>
                        </div>
                    </slot>
                </header>

                <!-- Body -->
                <div class="sb-body" ref="body">
                    <!-- your sections go here -->
                    <slot />
                </div>
            </aside>
        </transition>
    </div>
</template>
  
<script>
// Pure Vue 2 overlay sidebar (no BootstrapVue)
export default {
    name: 'SidebarOverlay',
    props: {
        value: { type: Boolean, default: false }, // v-model
        title: { type: String, default: '' },
        ariaLabel: { type: String, default: 'Sidebar' },
        closeOnBackdrop: { type: Boolean, default: true },
        width: { type: String, default: '15rem' }, // keep your rhythm
        zIndex: { type: Number, default: 1200 },
    },
    computed: {
        visible: {
            get() { return this.value; },
            set(v) { this.$emit('input', v); },
        },
        $tClose() {
            // change if you want i18n
            try { return this.$i18n?.t?.('close') ?? 'Close'; } catch { return 'Close'; }
        },
    },
    watch: {
        visible(v) {
            this.toggleBodyScroll(v);
            this.$emit(v ? 'show' : 'hide');
            if (v) this.$nextTick(() => this.focusPanel());
        },
        // auto-hide on route change (nice on mobile)
        $route() {
            if (this.visible) this.hide();
        },
    },
    mounted() {
        if (this.visible) this.toggleBodyScroll(true);
    },
    beforeDestroy() {
        this.toggleBodyScroll(false);
    },
    methods: {
        show() { this.visible = true; },
        hide() { this.visible = false; },
        closeByBackdrop() {
            if (this.closeOnBackdrop) this.hide();
        },
        toggleBodyScroll(lock) {
            // naive lock; if you already use a scroll-lock lib, swap here
            const cls = 'sb-lock-scroll';
            const body = document.body;
            if (lock) body.classList.add(cls);
            else body.classList.remove(cls);
        },
        focusPanel() {
            // focus the close button first for accessibility
            const btn = this.$el?.querySelector('.sb-iconbtn');
            if (btn) btn.focus();
        },
    },
};
</script>
  
<style scoped>
/* ===== Overlay & transitions ===== */

.sb-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .45);
    z-index: 1199;
    /* one below panel (default 1200) */
}

.sb-panel {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: var(--sb-width, 15rem);
    max-width: 90vw;
    background: rgba(var(--tmHubTheme-primary-color), 0.95);
    /* matches your mobile .b-sidebar */
    box-shadow: 0 0 20px rgba(0, 0, 0, .4);
    display: flex;
    flex-direction: column;
    z-index: 1200;
}

/* slide in from left */
.sb-slide-enter-active,
.sb-slide-leave-active {
    transition: transform .2s ease;
}

.sb-slide-enter,
.sb-slide-leave-to {
    transform: translateX(-100%);
}

/* backdrop fade */
.sb-fade-enter-active,
.sb-fade-leave-active {
    transition: opacity .18s ease;
}

.sb-fade-enter,
.sb-fade-leave-to {
    opacity: 0;
}

/* ===== Header ===== */
.sb-header {
    height: 3rem;
    display: flex;
    align-items: center;
    padding: 0 .5rem 0 .25rem;
    border-bottom: 1px solid rgba(255, 255, 255, .08);
}

.sb-title {
    margin-left: .25rem;
    font-weight: 600;
    color: #e6edf3;
}

.sb-iconbtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 0;
    background: transparent;
    color: #c9d1d9;
    border-radius: .375rem;
    cursor: pointer;
}

.sb-iconbtn:hover {
    background: rgba(255, 255, 255, .06);
    color: #fff;
}

/* ===== Body (scroll area) ===== */
.sb-body {
    flex: 1 1 auto;
    overflow: auto;
    padding: .75rem .5rem 1rem .75rem;
    /* keep your original trick */
}


/* lock scroll on body while open (global) */</style>
  
  <!-- global (unscoped) helper for scroll lock -->
<style>.sb-lock-scroll {
    overflow: hidden;
}</style>
  
  <!-- dynamic width / zIndex via CSS vars -->
<style scoped>.sb-panel {
    --sb-width: 17rem;
}</style>
  